/**
 * Wire-format types for the animachord REST + WebSocket clients.
 *
 * Two tracks live here:
 *
 *   1. REST resource shapes (Score, Take, Workspace, ApiToken, ...) — the
 *      minimum a v1 console needs to render. These are intentionally narrow:
 *      anything Score-related re-uses `@animachord/content-spec`.
 *   2. The `StageMessage` discriminated union — wire format for the live
 *      take WebSocket. Single source of truth for the 14 message kinds,
 *      mirroring `backend-contracts.md` §2 verbatim. Narrowed shapes only,
 *      no `any`.
 */

import type {
  ApiTokenId,
  LedgerRowId,
  ScoreId,
  TakeId,
  UserId,
  WorkspaceId,
} from "@animachord/content-spec";
import type { ApiErrorEnvelope } from "./errors";

// ============================================================================
// REST resource shapes (v1 minimum)
// ============================================================================

/** A Workspace is the multi-tenant boundary; one Auth0 user has 1+. */
export interface Workspace {
  readonly id: WorkspaceId;
  readonly name: string;
  readonly created_at: string;
}

/** Authenticated console user. */
export interface User {
  readonly id: UserId;
  readonly email: string;
  readonly name: string;
  readonly default_workspace_id: WorkspaceId;
}

/**
 * Score summary as returned by `GET /v1/scores`. The full score body
 * (manifest + cue sheet) is fetched separately and validated against the
 * `@animachord/content-spec` schemas.
 */
export interface ScoreSummary {
  readonly id: ScoreId;
  readonly workspace_id: WorkspaceId;
  readonly title: string;
  readonly status: "draft" | "rehearsable" | "performed" | "archived";
  readonly created_at: string;
  readonly updated_at: string;
}

/** Take summary as returned by `GET /v1/scores/:id/takes`. */
export interface TakeSummary {
  readonly id: TakeId;
  readonly score_id: ScoreId;
  readonly started_at: string;
  readonly ended_at: string | null;
  readonly note_count: number;
  readonly cost_usd: number;
}

/**
 * Score-token record. The plaintext key is returned **only** at creation;
 * subsequent `GET /v1/tokens` returns `last4` and `created_at`.
 */
export interface ApiToken {
  readonly id: ApiTokenId;
  readonly workspace_id: WorkspaceId;
  readonly name: string;
  readonly last4: string;
  readonly created_at: string;
  readonly revoked_at: string | null;
}

/** Plaintext key returned exactly once on create; never persisted client-side. */
export interface ApiTokenWithSecret extends ApiToken {
  readonly plaintext: string;
}

/** Per-day usage rollup row from `GET /v1/ledger`. */
export interface LedgerRow {
  readonly id: LedgerRowId;
  readonly workspace_id: WorkspaceId;
  readonly day: string;
  readonly notes: number;
  readonly minutes: number;
  readonly mb_hours: number;
  readonly cost_usd: number;
}

/** Single usage event; pre-aggregation. Used by debug surfaces. */
export interface UsageEvent {
  readonly id: string;
  readonly workspace_id: WorkspaceId;
  readonly kind: "note" | "minute" | "mb-hour";
  readonly amount: number;
  readonly cost_usd: number;
  readonly ts: string;
}

/** Quota for a given period; surfaced on the billing page. */
export interface Quota {
  readonly workspace_id: WorkspaceId;
  readonly period_start: string;
  readonly period_end: string;
  readonly notes_limit: number;
  readonly notes_used: number;
  readonly minutes_limit: number;
  readonly minutes_used: number;
}

/** Cursor-paginated list response per `backend-contracts.md` §0.5. */
export interface CursorPage<T> {
  readonly items: readonly T[];
  readonly next_cursor: string | null;
  readonly has_more: boolean;
}

// ============================================================================
// StageMessage discriminated union (WebSocket wire format)
// ============================================================================

/** Common envelope; every `StageMessage` extends it. */
export interface StageMessageEnvelope {
  readonly id: string;
  readonly ts: string;
  readonly kind: StageMessage["kind"];
}

// ---- server.* lifecycle ----------------------------------------------------

export interface VoiceLamp {
  readonly voice_id: string;
  readonly name: string;
  readonly active: boolean;
  readonly bond: number;
}

export interface StageHelloFromServer extends StageMessageEnvelope {
  readonly kind: "server.hello";
  readonly take_id: string;
  readonly voices: readonly VoiceLamp[];
  readonly resume_cursor: string | null;
  /**
   * Whether this socket recognised the visitor from their cookie.
   *
   * Optional because a server that predates the feature does not send
   * it, and its absence means "not recognised" rather than "unknown" —
   * recognition is a capability a server either has or does not.
   *
   * The control in the rehearsal bar reads
   * `GET /v1/rehearsal/remember`, not this. The two answer different
   * questions and can legitimately disagree: the endpoint says whether
   * this browser holds a cookie, and this says whether the characters
   * in `voices` are carrying regard from an earlier visit. When they
   * disagree this one is right, because it is reported by the thing
   * that either restored a history or did not.
   */
  readonly remembered?: boolean;
}

/**
 * Who is in the room, when that changes mid-take.
 *
 * `server.hello.voices` carries the cast at connect; this carries it
 * again after a visitor creates someone, so a new character is never
 * inferred from the first thing they say. A character who has not spoken
 * yet would otherwise be invisible and unselectable.
 */
export interface StageCastEvent extends StageMessageEnvelope {
  readonly kind: "server.cast";
  readonly voices: readonly VoiceLamp[];
}

export interface StageHeartbeat extends StageMessageEnvelope {
  readonly kind: "server.heartbeat" | "client.heartbeat";
  readonly rtt_ms?: number;
}

// ---- server.* stage events -------------------------------------------------

/**
 * Why a terminal note carries no text. Three facts, not one blank.
 *
 * - `not_asked` — the visitor sent a whitespace-only note. No model ran
 *   and nothing was spent.
 * - `not_heard` — a spoken turn the recognizer could not resolve. The
 *   audio leg is billed; the model was never asked.
 * - `no_reply` — **the model was asked and returned nothing**, and the
 *   turn is billed in full for the input it consumed.
 *
 * The first two are ordinary. The third is a defect (animachord#98: one
 * arc in 40 on the deep tier, none in 52 on the shipped one), and
 * until this field existed all three arrived as `text: ""` with nothing
 * to tell them apart — so a blank where the demo's payoff goes rendered
 * as a character choosing silence in answer to an apology.
 */
export type EmptyReplyReason = "not_asked" | "not_heard" | "no_reply";

export interface StageNoteEvent extends StageMessageEnvelope {
  readonly kind: "server.note";
  readonly voice_id: string;
  readonly text: string;
  readonly partial: boolean;
  /**
   * Present only on a terminal frame carrying no visible text. The
   * server omits the key entirely when there is text, so `undefined`
   * means "she replied", not "reason unknown" — the gateway serializes
   * with `exclude_none`, the same contract `recalled` documents.
   *
   * A one-word reply never carries this. Terse is not blank, and a UI
   * that treated them alike would flag the character's best turns.
   */
  readonly empty?: EmptyReplyReason;
  readonly parent_note_id?: string;
  readonly audio_url?: string;
  /**
   * Base64 MP3 of the spoken reply, present only on the terminal frame
   * of a turn the visitor spoke. Inline rather than behind `audio_url`
   * because a URL would need storage, a lifetime and an authorization
   * story of its own on the anonymous path, to deliver bytes this
   * socket is already authenticated to carry.
   */
  readonly audio_b64?: string;
}

/**
 * What the recognizer heard, echoed back before the reply.
 *
 * Its own frame rather than a `server.note`: a note is something a
 * Voice said, and this is something the visitor said. Collapsing them
 * would leave the UI unable to tell the character's words from its own
 * mishearing — exactly the distinction a visitor needs when the
 * transcript is wrong.
 */
export interface StageTranscriptEvent extends StageMessageEnvelope {
  readonly kind: "server.transcript";
  readonly text: string;
  readonly final: boolean;
}

/** Gesture from a Voice — payload is voice/pack-specific. */
export interface StageGestureEvent extends StageMessageEnvelope {
  readonly kind: "server.gesture";
  readonly voice_id: string;
  readonly gesture: Record<string, unknown>;
}

export interface StageReturnEvent extends StageMessageEnvelope {
  readonly kind: "server.return";
  readonly voice_id: string;
  readonly gesture_id: string;
  readonly ok: boolean;
  readonly result_summary: string;
}

export type WrongNoteCategory =
  | "safety"
  | "character-break"
  | "tool-error"
  | "rate-limit"
  | "internal";

export interface StageWrongNoteEvent extends StageMessageEnvelope {
  readonly kind: "server.wrong-note";
  readonly voice_id: string;
  readonly category: WrongNoteCategory;
  readonly message: string;
}

export interface StageBondTickEvent extends StageMessageEnvelope {
  readonly kind: "server.bond-tick";
  readonly voice_id: string;
  /** 0.0-1.0. The engine's own scale is 0-100; the wire's is this one. */
  readonly bond: number;
  readonly delta: number;
  /** Plain words for what the visitor did. Panel copy, never a field name. */
  readonly reason?: string;
  /**
   * The cap an unrepaired rupture imposes, on the same 0.0-1.0 scale as
   * `bond` — the panel draws one against the other.
   *
   * **`null` when nothing has ruptured, never `0`.** A zero renders "she
   * will not go past 0" under every unruptured relationship, which is a
   * false claim about a character in the one surface built to be checked.
   */
  readonly rupture_floor?: number | null;
  /*
   * `repair_progress` and `repair_required` were here and are gone, on a
   * brand lock: a visitor told the exit condition optimises against it.
   * They are being removed from the frame too. `rupture_floor` stays --
   * it says she is holding back without saying what would stop her.
   */
}

export interface StageFadeTickEvent extends StageMessageEnvelope {
  readonly kind: "server.fade-tick";
  readonly voice_id: string;
  readonly resonance_strengths: ReadonlyArray<{
    readonly resonance_id: string;
    readonly strength: number;
  }>;
}

export interface StageTuningEvent extends StageMessageEnvelope {
  readonly kind: "server.tuning";
  readonly voice_id?: string;
  readonly message: string;
}

export type EndTakeReason =
  | "user-ended"
  | "time-budget"
  | "note-budget"
  | "wrong-note"
  | "server-shutdown";

export interface EndTakeSummary {
  readonly duration_ms: number;
  readonly note_count: number;
  readonly bond_final: ReadonlyArray<{
    readonly voice_id: string;
    readonly bond: number;
  }>;
  readonly wrong_note_count: number;
  readonly cost_usd: number;
}

export interface StageEndTakeEvent extends StageMessageEnvelope {
  readonly kind: "server.end-take";
  readonly reason: EndTakeReason;
  readonly take_summary: EndTakeSummary;
}

// ---- client.* actions ------------------------------------------------------

export interface StageHumanNoteAction extends StageMessageEnvelope {
  readonly kind: "client.human-note";
  readonly text: string;
  readonly voice_id?: string;
}

export interface StageHumanAudioAction extends StageMessageEnvelope {
  readonly kind: "client.human-audio";
  readonly audio_b64: string;
  readonly voice_id?: string;
}

export interface StageCueAction extends StageMessageEnvelope {
  readonly kind: "client.cue";
  readonly dial_id: string;
  readonly value: number;
}

export interface StageInterruptAction extends StageMessageEnvelope {
  readonly kind: "client.interrupt";
  readonly voice_id: string;
}

/**
 * One sentence in, one character out.
 *
 * The sentence is the whole input. Anything more is a form, and a visitor
 * given a form in the first minute fills none of it in. The server bounds
 * it at 400 characters and refuses past that before spending anything.
 */
export interface StageCreateCharacterAction extends StageMessageEnvelope {
  readonly kind: "client.create-character";
  readonly sentence: string;
}

// ---- bidirectional error ---------------------------------------------------

export interface StageErrorMessage extends StageMessageEnvelope {
  readonly kind: "server.error" | "client.error";
  readonly error: ApiErrorEnvelope;
  readonly fatal: boolean;
  readonly close_code?: 1008 | 1011 | 1013;
}

/**
 * The full discriminated union. 15 distinct `kind` values (heartbeat and
 * error are bidirectional but each emits a single shape).
 */
/** One speaker in a piece of gossip's history, as it arrives on the wire. */
export interface GossipHop {
  readonly speaker: string;
  /**
   * What that speaker was carrying when they passed it on, not the
   * item's confidence now. The panel's "she is less sure of it than he
   * was" is the difference between two of these, so flattening them to
   * the current value would render the same sentence at every hop.
   */
  readonly confidence: number;
  readonly at: string;
}

export type GossipEventKind = "heard" | "told" | "withheld";

/**
 * Something that happened to the social graph, for the state panel.
 *
 * `withheld` is the one that carries the argument. A dropped item shown
 * with the reason it was dropped is more convincing than a delivered
 * one, because discretion is the thing a shortcut cannot fake, so it is
 * a first-class event here rather than an absence the panel would have
 * to infer.
 */
export interface StageGossipEvent extends StageMessageEnvelope {
  readonly kind: "server.gossip";
  readonly event: GossipEventKind;
  readonly content: string;
  /** Plain words, present only on `withheld`. Never a field name. */
  readonly reason?: string;
  readonly chain?: readonly GossipHop[];
  readonly hops?: number;
  readonly confidence?: number;
  readonly told_by?: string;
  readonly to_agent?: string;
}

/**
 * One thing she recalled, as it arrives on the wire.
 *
 * Mirrors `RecalledMemory` in `stage_ws/protocol.py`, including what that
 * model deliberately leaves out.
 *
 * There is no `content`: the wire carries a 140-character `preview` and
 * the whole record goes only to the prompt. And there is no
 * `relevance_score`, which the producing chunk does carry and which is
 * always `null` there, because the backends discard the score they
 * computed before this seam. A field that is only ever null is one a
 * consumer eventually renders as a zero, and "relevance 0" beside
 * something she genuinely recalled is worse than saying nothing.
 */
export interface RecalledMemory {
  readonly id: string;
  /** `episodic`, `core`, `semantic`, `procedural`, `resource`, `vault`, or "". */
  readonly memory_type?: string;
  /** ISO-8601, from when the record was written. */
  readonly stored_at?: string | null;
  /** At most 140 characters. Never the whole record. */
  readonly preview?: string;
  /**
   * Ebbinghaus retention, 0 to 1, and the only input to the panel's tiers.
   *
   * Optional because not every producer computes it -- the Swift client's
   * ref emits the other four fields and not this one. Absent must not
   * become `0`, which would render everything she holds as nearly gone.
   */
  readonly retention?: number | null;
}

/**
 * What she recalled on this turn.
 *
 * `recalled` is nullable and the null carries a fact. `null` means
 * retrieval did not run and the panel may claim nothing; `[]` means it
 * ran and matched nothing on this turn; a list means she recalled these.
 * The producer maintains that distinction end to end and the panel is
 * the first consumer that can show it.
 *
 * `remembered` is what separates "she looked and found nothing about
 * this" from "she has nothing of yours to hold yet" -- both retrieve an
 * empty list, and only the second sentence should ever be shown to a
 * visitor whose memories are not being kept.
 */
export interface StageRecallEvent extends StageMessageEnvelope {
  readonly kind: "server.recall";
  readonly voice_id: string;
  readonly recalled?: readonly RecalledMemory[] | null;
  readonly remembered?: boolean;
}

export type StageMessage =
  | StageHelloFromServer
  | StageCastEvent
  | StageHeartbeat
  | StageNoteEvent
  | StageTranscriptEvent
  | StageGestureEvent
  | StageReturnEvent
  | StageWrongNoteEvent
  | StageBondTickEvent
  | StageFadeTickEvent
  | StageTuningEvent
  | StageGossipEvent
  | StageRecallEvent
  | StageEndTakeEvent
  | StageHumanNoteAction
  | StageHumanAudioAction
  | StageCueAction
  | StageInterruptAction
  | StageCreateCharacterAction
  | StageErrorMessage;

/** All recognised `kind` discriminants — used for runtime validation. */
export const STAGE_MESSAGE_KINDS = [
  "server.hello",
  "server.heartbeat",
  "client.heartbeat",
  "server.note",
  "server.transcript",
  "server.gesture",
  "server.return",
  "server.wrong-note",
  "server.bond-tick",
  "server.fade-tick",
  "server.tuning",
  "server.gossip",
  "server.recall",
  "server.end-take",
  "client.human-note",
  "client.human-audio",
  "client.cue",
  "client.interrupt",
  "client.create-character",
  "server.cast",
  "server.error",
  "client.error",
] as const;

export type StageMessageKind = (typeof STAGE_MESSAGE_KINDS)[number];

/**
 * Compile-time proof that the runtime list above covers the union.
 *
 * `StageSocketClient` narrows inbound JSON against this array and drops
 * anything not in it, and `StageMessageKind` is derived *from* the array,
 * so a union member whose kind was never listed type-checks perfectly and
 * is discarded at the transport. That happened: `server.cast` was added
 * to `StageMessage`, the console compiled, and the frame never reached a
 * handler. The page looked broken and nothing anywhere went red.
 *
 * This line fails to compile the moment a member's `kind` is missing,
 * which is the difference between a rule and a guarantee. The runtime
 * half is asserted in `tests/api/stage-message-kinds.test.ts`, because a
 * type that is never instantiated is not evidence the array shipped.
 */
type UnlistedStageKind = Exclude<StageMessage["kind"], StageMessageKind>;
const _everyKindIsListed: UnlistedStageKind extends never ? true : never = true;
void _everyKindIsListed;

/** Connection state machine for the StageSocketClient. */
export type StageSocketState =
  | "connecting"
  | "open"
  | "closing"
  | "closed"
  | "reconnecting";
